import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, Zap, Crown } from "lucide-react";

export default function Pricing() {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const isPro = !!user?.is_pro;

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setLoading(false);
    }
  };

  const proFeatures = [
    "Unlimited music videos",
    "Full-length rendering (Sora 2)",
    "Full 4-minute AI songs",
    "AI storyboard & lyric generation",
    "Upload your own audio and photos",
    "HD, vertical, square exports",
    "Priority render queue",
  ];

  return (
    <div>
      <Nav />
      <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-16">
        <div className="mb-8 md:mb-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFE800]">// Pricing</div>
          <h1 className="font-display text-5xl leading-none sm:text-6xl md:text-7xl">
            Fifteen Ninety-Nine.
            <br />
            <span className="text-[#FF003C]">Every Video You Want.</span>
          </h1>
        </div>

        <div className="grid gap-0 md:grid-cols-2">
          {/* FREE */}
          <div className="border border-[#222] p-6 md:p-8" data-testid="pricing-free">
            <div className="font-mono text-xs uppercase tracking-widest text-[#808080]">Free</div>
            <div className="mt-2 font-display text-6xl">$0</div>
            <div className="text-sm text-[#808080]">Try it out</div>
            <ul className="mt-8 space-y-3 text-sm">
              {["1 test video", "30-second AI song preview", "Storyboard preview"].map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check size={14} className="text-[#00F0FF]" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-10 font-mono text-xs uppercase tracking-widest text-[#808080]">
              No signup needed
            </div>
          </div>

          {/* PRO */}
          <div
            className="relative border border-t-0 border-[#FF003C] bg-[#0A0A0B] p-6 pt-8 md:border-l-0 md:border-t md:p-8"
            data-testid="pricing-pro"
          >
            <div className="absolute -top-3 left-6 bg-[#FF003C] px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
              Most Popular
            </div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#FFE800]">Pro</div>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <div className="font-display text-5xl text-[#FF003C] sm:text-6xl" data-testid="pricing-pro-price">$15.99</div>
              <div className="font-mono text-sm text-[#808080]">/month</div>
            </div>
            <div className="text-sm text-white">Everything, unlimited</div>
            <ul className="mt-8 space-y-3 text-sm">
              {proFeatures.map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check size={14} className="text-[#FFE800]" /> {f}
                </li>
              ))}
            </ul>
            {isPro ? (
              <Link to="/studio" data-testid="pricing-already-pro" className="btn-brutal mt-10 w-full bg-[#FFE800] text-black">
                <Crown size={16} /> You're Pro · Open Studio
              </Link>
            ) : (
              <button
                data-testid="pricing-subscribe-btn"
                onClick={checkout}
                disabled={loading}
                className="btn-brutal mt-10 w-full"
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                Subscribe · $15.99/mo
              </button>
            )}
            <div className="mt-4 flex items-center gap-2 border border-[#222] p-3" data-testid="pricing-payment-methods">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#808080]">Pay with</div>
              <div className="flex items-center gap-2 font-display text-sm">
                <span className="border border-[#222] px-2 py-1 text-white">CARD</span>
                <span className="border border-[#222] px-2 py-1 text-[#00F0FF]">CASH APP</span>
                <span className="border border-[#222] px-2 py-1 text-[#808080]">LINK</span>
              </div>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#808080]">
              Secure checkout powered by Stripe · Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
